import type { TimeFilter } from "@/components/v2/DatePicker/constants";
import { ReportingSortingDirection } from "@/pages/reporting_v2/types";
import { translate } from "@/translations/functions/hook";

import { sum } from "lodash";
import moment from "moment-timezone";
import { Granularity } from "server/dwhApi/common.js";
import type { Customer } from "server/dwhApi/customers/types.js";
import type { Terminal } from "server/dwhApi/terminals/types.js";

import type {
  ReportingTableHeader,
  TableRow,
  UnitConverter,
  WasteColumn,
  WasteSource,
} from "../../types";
import {
  NAME_COLUMN_ID,
  RATE_COLUMN_ID,
  TOTAL_COLUMN_ID,
  ZONE_COLUMN_ID,
} from "./headers";

const buildRow = (
  key: string,
  name: string,
  source: WasteSource,
  columns: WasteColumn[],
  convert: UnitConverter,
  extra: Partial<TableRow> = {},
): TableRow => {
  const weights = Object.fromEntries(
    columns.map(({ code }) => [
      code,
      source.wasteTypes.find((wt) => wt.code === code)?.weight.quantity ?? 0,
    ]),
  );
  const cells = Object.fromEntries(
    columns.map(({ code }) => [code, convert(code, weights[code])]),
  );

  return {
    key,
    name,
    cells,
    weights,
    total: sum(Object.values(cells)),
    rate: (source.sortingDegree || 0) * 100,
    totalWeight: source.totalWasteWeight.quantity,
    residualWeight: source.totalResidualWasteWeight.quantity,
    ...extra,
  };
};

const getDateRange = (startTime: string, endTime: string, timezone: string) => {
  const start = moment(startTime).tz(timezone);
  // Subtracting 1ms gives the right last day for both exclusive and inclusive end times.
  const end = moment(endTime).tz(timezone).subtract(1, "ms");
  return `${start.format("DD.MM")} – ${end.format("DD.MM")}`;
};

const getPeriodLabel = (
  startTime: string,
  granularity: Granularity,
  language: string,
  timezone: string,
) => {
  const start = moment(startTime).tz(timezone).locale(language);
  switch (granularity) {
    case Granularity.NONE:
      return { name: translate("datePicker:presetLabelAllTime") };
    case Granularity.YEAR:
      return { name: start.format("YYYY") };
    case Granularity.QUARTER:
      return { name: `Q${start.quarter()}`, subLabel: start.format("YYYY") };
    case Granularity.MONTH:
      return { name: start.format("MMM"), subLabel: start.format("YYYY") };
    case Granularity.DAY:
      return { name: start.format("DD MMM"), subLabel: start.format("YYYY") };
    default:
      return {
        name: translate("reportingPage:weekPeriodLabel", {
          weekNr: start.isoWeek(),
        }),
        subLabel: String(start.isoWeekYear()),
      };
  }
};

export const buildPeriodRows = (
  periods: { startTime: string; endTime: string; source: WasteSource }[],
  granularity: Granularity,
  columns: WasteColumn[],
  convert: UnitConverter,
  language: string,
  timezone: string,
  timeFilter?: TimeFilter,
) =>
  periods.map(({ startTime, endTime, source }, index) => {
    const { name, subLabel } = getPeriodLabel(
      startTime,
      granularity,
      language,
      timezone,
    );
    const showDateRange =
      granularity !== Granularity.NONE && granularity !== Granularity.DAY;
    // Edge periods can be partial, so their labels follow the selected time filter.
    const rangeStart = (index === 0 && timeFilter?.startTimeISO) || startTime;
    const rangeEnd =
      (index === periods.length - 1 && timeFilter?.endTimeISO) || endTime;
    return buildRow(startTime, name, source, columns, convert, {
      subLabel,
      dateRange: showDateRange
        ? getDateRange(rangeStart, rangeEnd, timezone)
        : undefined,
      startTime,
      endTime,
    });
  });

/** Portfolio by building: one row per terminal. */
export const buildTerminalRows = (
  terminals: Terminal[],
  columns: WasteColumn[],
  convert: UnitConverter,
) =>
  terminals.map((terminal) =>
    buildRow(
      terminal.terminalId,
      terminal.terminalName,
      terminal,
      columns,
      convert,
      { id: terminal.terminalId },
    ),
  );

export const buildCustomerRows = (
  customers: Customer[],
  showZones: boolean,
  columns: WasteColumn[],
  convert: UnitConverter,
) =>
  customers
    .filter((customer) => !!customer.customerId)
    .flatMap((customer) => {
      if (showZones && customer.children?.length) {
        return customer.children.map((unit) =>
          buildRow(
            `${customer.customerId}|${unit.customerId}`,
            customer.customerName,
            unit,
            columns,
            convert,
            { id: unit.customerId, zone: unit.customerName },
          ),
        );
      }
      return [
        buildRow(
          customer.customerId,
          customer.customerName,
          customer,
          columns,
          convert,
          { id: customer.customerId },
        ),
      ];
    });

export const sortRows = (rows: TableRow[], headers: ReportingTableHeader[]) => {
  const activeSort = headers.find((header) => header.isSortingActive);
  if (!activeSort) return rows;

  const direction =
    activeSort.sortingDirection === ReportingSortingDirection.DESC ? -1 : 1;
  const sortValue = (row: TableRow): number | string => {
    switch (activeSort.id) {
      case NAME_COLUMN_ID:
        return row.name;
      case ZONE_COLUMN_ID:
        return row.zone ?? "";
      case RATE_COLUMN_ID:
        return row.rate;
      case TOTAL_COLUMN_ID:
        return row.total;
      default:
        return row.cells[activeSort.id] ?? 0;
    }
  };

  return [...rows].sort((a, b) => {
    const va = sortValue(a);
    const vb = sortValue(b);
    const result =
      typeof va === "string" && typeof vb === "string"
        ? va.localeCompare(vb) || (a.zone ?? "").localeCompare(b.zone ?? "")
        : (va as number) - (vb as number);
    return result * direction;
  });
};

export const buildTotalsRow = (
  rows: TableRow[],
  columns: WasteColumn[],
): TableRow => {
  const sumBy = (field: "cells" | "weights") =>
    Object.fromEntries(
      columns.map(({ code }) => [
        code,
        sum(rows.map((row) => row[field][code])),
      ]),
    );
  const cells = sumBy("cells");
  const totalWeight = sum(rows.map((row) => row.totalWeight));
  const residualWeight = sum(rows.map((row) => row.residualWeight));

  return {
    key: "totals",
    name: "",
    cells,
    weights: sumBy("weights"),
    total: sum(Object.values(cells)),
    rate: totalWeight > 0 ? (1 - residualWeight / totalWeight) * 100 : 0,
    totalWeight,
    residualWeight,
  };
};
